import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { fmtRub } from "@/lib/api";

const COLORS = ["#0084ff", "#00e676", "#ffea00", "#a970ff", "#ff9100", "#ff3b30", "#4dabf5", "#00bfa5"];
const tooltipStyle = {
  contentStyle: { background: "hsl(220 10% 12%)", border: "1px solid hsl(220 10% 22%)", borderRadius: 8, fontSize: 12 },
  labelStyle: { color: "hsl(220 10% 70%)" },
};
const axis = { stroke: "hsl(220 10% 40%)", fontSize: 11, tickLine: false, axisLine: false };
const grid = <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 10% 16%)" vertical={false} />;
const monthNames = ["Янв", "Фев", "Мар", "Апр", "Май", "Июн", "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек"];
const fmtMonth = (m) => monthNames[parseInt(m.slice(5, 7), 10) - 1];
const rub = (v) => fmtRub(v);

export default function BudgetCharts({ txs, byCategory, trend }) {
  const dayMap = {};
  txs.forEach((t) => {
    const d = t.date.slice(8, 10);
    dayMap[d] = dayMap[d] || { day: d, income: 0, expense: 0 };
    dayMap[d][t.type] += t.amount;
  });
  const daily = Object.values(dayMap).sort((a, b) => a.day.localeCompare(b.day));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6" data-testid="budget-charts">
      <div className="glass-card p-5" data-testid="budget-donut-chart">
        <h3 className="text-sm font-semibold mb-2">Структура расходов</h3>
        {byCategory.length === 0 ? (
          <p className="text-sm text-muted-foreground py-10 text-center">Нет расходов</p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={byCategory} dataKey="amount" nameKey="category" innerRadius={55} outerRadius={85} paddingAngle={2} strokeWidth={0}>
                {byCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip {...tooltipStyle} formatter={(v) => rub(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="glass-card p-5" data-testid="budget-daily-chart">
        <h3 className="text-sm font-semibold mb-2">По дням месяца</h3>
        {daily.length === 0 ? (
          <p className="text-sm text-muted-foreground py-10 text-center">Нет операций</p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={daily}>
              {grid}
              <XAxis dataKey="day" {...axis} />
              <YAxis {...axis} width={45} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}к` : v)} />
              <Tooltip {...tooltipStyle} formatter={(v) => rub(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="income" name="Доход" fill="#00e676" fillOpacity={0.8} radius={[3, 3, 0, 0]} />
              <Bar dataKey="expense" name="Расход" fill="#ff3b30" fillOpacity={0.8} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="glass-card p-5" data-testid="budget-trend-chart">
        <h3 className="text-sm font-semibold mb-2">Динамика за 6 месяцев</h3>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={trend}>
            {grid}
            <XAxis dataKey="month" tickFormatter={fmtMonth} {...axis} />
            <YAxis {...axis} width={45} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}к` : v)} />
            <Tooltip {...tooltipStyle} formatter={(v) => rub(v)} labelFormatter={fmtMonth} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="income" name="Доходы" stroke="#00e676" strokeWidth={2} fill="#00e676" fillOpacity={0.12} />
            <Area type="monotone" dataKey="expense" name="Расходы" stroke="#ff3b30" strokeWidth={2} fill="#ff3b30" fillOpacity={0.12} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
